import { useRef } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { TASK_IDS } from "@/shared/constants/taskIds";

export interface EstateSizeAnswers {
  "property-ownerships"?: Record<number, string>;
  "estate-amount"?: "under" | "over" | "not-sure";
  "estate-threshold"?: number;
  "estate-threshold-state"?: string | null;
}

export function useEstateSizeAnswers(estateId: number | undefined) {
  const queryClient = useQueryClient();

  const { data: savedAnswers, isFetched } = useQuery({
    queryKey: ["estateSizeAnswers", estateId],
    queryFn: async () => {
      const { data } = await supabase
        .from("estate_tasks")
        .select("answers")
        .eq("estate_id", estateId!)
        .eq("task_id", TASK_IDS.ESTATE_SIZE)
        .maybeSingle();
      return (data?.answers as EstateSizeAnswers | null) ?? null;
    },
    enabled: !!estateId,
    staleTime: 0,
  });

  const saveMutation = useMutation({
    mutationFn: async (answers: EstateSizeAnswers) => {
      if (!estateId) return;
      const { error } = await supabase.from("estate_tasks").upsert(
        {
          estate_id: estateId,
          task_id: TASK_IDS.ESTATE_SIZE,
          status: "in_progress",
          answers,
          last_active_at: new Date().toISOString(),
        },
        { onConflict: "estate_id,task_id" },
      );
      if (error) throw error;
    },
    onMutate: async (answers) => {
      await queryClient.cancelQueries({ queryKey: ["estateSizeAnswers", estateId] });
      queryClient.setQueryData(["estateSizeAnswers", estateId], answers);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["estateSizeAnswers", estateId] });
    },
  });

  const saveQueue = useRef<Promise<unknown>>(Promise.resolve());
  const lastSaveError = useRef<unknown>(null);

  const mergeAndSave = (partial: Partial<EstateSizeAnswers>) => {
    const next = saveQueue.current.then(async () => {
      lastSaveError.current = null;
      const latest = queryClient.getQueryData<EstateSizeAnswers | null>([
        "estateSizeAnswers",
        estateId,
      ]);
      const merged = { ...latest, ...partial } as EstateSizeAnswers;
      return saveMutation.mutateAsync(merged);
    });
    saveQueue.current = next.catch((err) => {
      lastSaveError.current = err;
    });
    return next;
  };

  const completeTask = async () => {
    if (!estateId) return;
    await saveQueue.current;
    if (lastSaveError.current) throw lastSaveError.current;
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from("estate_tasks")
      .update({
        status: "completed",
        completed_at: now,
        last_active_at: now,
      })
      .eq("estate_id", estateId)
      .eq("task_id", TASK_IDS.ESTATE_SIZE)
      .not("answers", "is", null)
      .select("id")
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error("Cannot complete task: answers not persisted");
    queryClient.invalidateQueries({ queryKey: ["estateSizeAnswers", estateId] });
    queryClient.invalidateQueries({ queryKey: ["moduleProgress", estateId] });
  };

  return {
    savedAnswers,
    isFetched,
    saveAnswers: mergeAndSave,
    completeTask,
  };
}
